import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";

/**
 * Publicly-viewable splash page. Fetched by `slug`. Records a view on mount
 * and a click each time a link/button/image link is followed.
 */
export default function PublicSplash() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | not_found | error

  useEffect(() => {
    if (!slug) return;
    (async () => {
      try {
        const { data } = await api.get(`/splash/public/${encodeURIComponent(slug)}`);
        setPage(data);
        setStatus("ok");
        document.title = data.title || "Splash";
      } catch (e) {
        setStatus(e.response?.status === 404 ? "not_found" : "error");
      }
    })();
  }, [slug]);

  const widthClass = useMemo(() => ({
    sm: "max-w-md",
    md: "max-w-2xl",
    lg: "max-w-4xl",
    full: "max-w-full",
  }[page?.max_width || "md"] || "max-w-2xl"), [page?.max_width]);

  const recordClick = async (blockId) => {
    // Best-effort — never block navigation on the analytics call.
    try {
      await api.post(`/splash/public/${encodeURIComponent(slug)}/click`, { block_id: blockId });
    } catch (_) { /* ignore */ }
  };

  if (status === "loading") {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  }

  if (status === "not_found") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center p-6 bg-slate-50 text-slate-900">
        <div className="text-6xl font-black tracking-tight">404</div>
        <p className="mt-2 text-slate-500">This splash page doesn&rsquo;t exist or has been deleted.</p>
        <Link to="/" className="mt-4 text-blue-600 hover:underline text-sm">Go home →</Link>
      </div>
    );
  }

  if (status === "error" || !page) {
    return <div className="min-h-screen flex items-center justify-center text-red-600">Failed to load splash page.</div>;
  }

  return (
    <div className="min-h-screen w-full" style={{ background: page.background || "#ffffff", color: page.text_color || "#0f172a" }}>
      {/* Branded top bar — credited to the page owner via ?ref= */}
      <a
        data-testid="public-splash-top-bar"
        href={page.owner_referral_code
          ? `/register?ref=${encodeURIComponent(page.owner_referral_code)}`
          : "/"}
        className="group relative block w-full bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white text-center py-1.5 sm:py-2 px-4 hover:from-black hover:to-black transition-colors border-b-2 border-blue-400 shadow-[0_3px_0_rgba(96,165,250,0.35)]"
        style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}
      >
        <div className="flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
          <span className="text-blue-400 text-lg sm:text-xl md:text-2xl font-black tracking-tight drop-shadow-[0_2px_0_rgba(0,0,0,0.5)] leading-none">
            Trafficexchange.app
          </span>
          <span className="hidden sm:inline text-blue-400/40 text-xl leading-none">•</span>
          <span className="text-white text-base sm:text-lg md:text-xl font-extrabold tracking-tight leading-none">
            Build your <span className="underline decoration-blue-400 decoration-4 underline-offset-2">FREE</span> splash page
          </span>
          <span
            aria-hidden="true"
            className="inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-blue-400 text-slate-900 text-base font-black group-hover:translate-x-1 transition-transform"
          >
            →
          </span>
        </div>
      </a>

      <div data-testid="public-splash" className={`mx-auto ${widthClass} px-4 py-12 space-y-6`}>
        {(page.blocks || []).map((b) => (
          <Block key={b.id} block={b} textColor={page.text_color} onClick={() => recordClick(b.id)} />
        ))}
      </div>
    </div>
  );
}

function Block({ block, textColor, onClick }) {
  const alignClass = { left: "text-left", center: "text-center", right: "text-right" }[block.align] || "text-center";
  const HEADING = { sm: "text-xl", md: "text-2xl", lg: "text-4xl", xl: "text-5xl md:text-6xl" };
  const TEXTS   = { sm: "text-xs", md: "text-base", lg: "text-lg", xl: "text-xl" };
  const BTN     = { sm: "px-3 py-1.5 text-xs", md: "px-4 py-2 text-sm", lg: "px-5 py-2.5 text-base", xl: "px-6 py-3 text-lg" };

  if (block.type === "heading") {
    const H = "h1";
    const el = (
      <H className={`font-black tracking-tight leading-tight ${HEADING[block.size] || HEADING.xl} ${alignClass}`}>
        {block.content}
      </H>
    );
    return block.link ? <a data-testid={`public-block-${block.id}`} href={block.link} target="_blank" rel="noreferrer noopener" onClick={onClick}>{el}</a> : el;
  }
  if (block.type === "text") {
    const el = <p className={`${TEXTS[block.size] || TEXTS.md} ${alignClass} opacity-90 leading-relaxed`}>{block.content}</p>;
    return block.link ? <a data-testid={`public-block-${block.id}`} href={block.link} target="_blank" rel="noreferrer noopener" onClick={onClick}>{el}</a> : el;
  }
  if (block.type === "image") {
    if (!block.image_url) return null;
    const img = <img src={block.image_url} alt="" className="mx-auto max-w-full h-auto rounded-lg" loading="lazy" />;
    return block.link
      ? <a data-testid={`public-block-${block.id}`} href={block.link} target="_blank" rel="noreferrer noopener" onClick={onClick} className={`block ${alignClass}`}>{img}</a>
      : <div className={alignClass}>{img}</div>;
  }
  if (block.type === "button") {
    return (
      <div className={alignClass}>
        <a
          data-testid={`public-block-${block.id}`}
          href={block.link || "#"}
          target="_blank"
          rel="noreferrer noopener"
          onClick={onClick}
          className={`inline-flex items-center rounded-full font-black tracking-wide shadow-lg hover:scale-105 transition-transform ${BTN[block.size] || BTN.md}`}
          style={{ background: textColor, color: "#0f172a" }}
        >
          {block.content || "Learn more"}
        </a>
      </div>
    );
  }
  if (block.type === "divider") {
    return <div className="my-4 flex items-center justify-center"><div className="h-px w-24 opacity-40" style={{ background: textColor }} /></div>;
  }
  return null;
}
